import React, { useState } from 'react';
import { Button } from './Button';
import { FadeIn } from './FadeIn';
import { ChevronDown, ChevronUp, Quote, Star } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
interface Testimonial {
  quote: string;
  name: string;
  detail: string;
}
const initialTestimonials: Testimonial[] = [{
  quote: 'Después de meses viendo contenido suelto, por fin encontré una ruta clara para entender cómo comenzar.',
  name: 'Javier M.',
  detail: 'Charlotte, NC'
}, {
  quote: 'El programa está organizado de una forma muy sencilla. Pude entender FBA y FBM sin sentirme perdido.',
  name: 'Carlos R.',
  detail: 'Miami, FL'
}, {
  quote: 'Mucho mejor de lo que esperaba. Todo está explicado paso a paso y con ejemplos fáciles de seguir.',
  name: 'Andrea G.',
  detail: 'Bogotá, Colombia'
}, {
  quote: 'Nunca había vendido en Amazon. Este sistema me dio la confianza para empezar desde cero.',
  name: 'Miguel A.',
  detail: 'Ciudad de México, México'
}, {
  quote: 'Una inversión que sí vale la pena. El contenido y los recursos adicionales están muy bien pensados.',
  name: 'Daniela P.',
  detail: 'Houston, TX'
}, {
  quote: 'Si hubiera encontrado Amazon Master System antes, habría evitado mucha confusión y errores.',
  name: 'Roberto L.',
  detail: 'Madrid, España'
}];
const additionalTestimonials: Testimonial[] = [{
  quote: 'Me dio la estructura que necesitaba para dejar de postergar el inicio.',
  name: 'Luis C.',
  detail: 'Orlando, FL'
}, {
  quote: 'Se nota que el material fue pensado para quienes comenzamos desde cero.',
  name: 'María S.',
  detail: 'Santiago, Chile'
}, {
  quote: 'Aprendí a mirar Amazon con más claridad y a saber qué pasos seguir primero.',
  name: 'Andrés T.',
  detail: 'Dallas, TX'
}, {
  quote: 'Las explicaciones son directas, ordenadas y fáciles de aplicar.',
  name: 'Paola R.',
  detail: 'Lima, Perú'
}, {
  quote: 'Los bonos complementan muy bien el programa principal.',
  name: 'José M.',
  detail: 'Buenos Aires, Argentina'
}, {
  quote: 'Lo recomiendo a cualquiera que quiera empezar correctamente.',
  name: 'Carolina V.',
  detail: 'New York, NY'
}];
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <article className="flex h-full flex-col rounded-2xl border border-brand-text/8 bg-white p-7 shadow-[0_12px_38px_-24px_rgba(26,26,26,0.22)] sm:p-8" data-id="element-934">
      <Quote className="mb-5 h-7 w-7 text-brand-gold/55" strokeWidth={2.5} data-id="element-935" />
      <div className="mb-4 flex gap-0.5" data-id="element-936">
        {[...Array(5)].map((_, index) => <Star key={index} className="h-4 w-4 fill-brand-gold text-brand-gold" data-id="element-937" />)}
      </div>
      <blockquote className="flex-grow text-base font-medium leading-relaxed text-brand-text/80" data-id="element-938">
        “{testimonial.quote}”
      </blockquote>
      <footer className="mt-7 border-t border-brand-text/8 pt-5" data-id="element-939">
        <p className="font-semibold text-brand-green" data-id="element-940">{testimonial.name}</p>
        <p className="mt-0.5 text-xs text-brand-text/50" data-id="element-941">
          {testimonial.detail}
        </p>
      </footer>
    </article>;
}
export function TestimoniosSistema() {
  const [showAll, setShowAll] = useState(false);
  return <section className="bg-brand-beige/65 px-5 py-20 sm:px-6 md:py-28" data-id="element-942">
      <div className="mx-auto max-w-6xl" data-id="element-943">
        <FadeIn data-id="element-944">
          <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-945">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold" data-id="element-946">
              Experiencias de estudiantes
            </p>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-brand-green sm:text-4xl md:text-5xl" data-id="element-947">
              La claridad para dar el primer paso.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-brand-text/65 sm:text-lg" data-id="element-948">
              Personas que decidieron dejar atrás la información dispersa y
              comenzar con una ruta de aprendizaje más organizada.
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-949">
          {initialTestimonials.map((testimonial, index) => <FadeIn key={testimonial.name} delay={0.05 * index} data-id="element-950">
              <TestimonialCard testimonial={testimonial} data-id="element-951" />
            </FadeIn>)}
        </div>

        <AnimatePresence initial={false} data-id="element-952">
          {showAll && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.4,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-953">
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-954">
                {additionalTestimonials.map((testimonial, index) => <motion.div key={testimonial.name} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: index * 0.04
            }} data-id="element-955">
                    <TestimonialCard testimonial={testimonial} data-id="element-956" />
                  </motion.div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        <div className="mt-12 text-center" data-id="element-957">
          <Button onClick={() => setShowAll(current => !current)} className="inline-flex items-center gap-2 bg-brand-green px-8 py-4 text-sm font-bold !text-white" data-id="element-958">
            {showAll ? 'VER MENOS OPINIONES' : 'VER MÁS OPINIONES'}
            {showAll ? <ChevronUp className="h-4 w-4" data-id="element-959" /> : <ChevronDown className="h-4 w-4" data-id="element-960" />}
          </Button>
        </div>
      </div>
    </section>;
}